import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { Button } from '@canvas-logic/clp-shared-ui';

import { useMediaQuery } from '@/ui-kit/hooks/use-media-query';
import { Placement, Popover, usePopoverRef } from '@/ui-kit/components/Popover';
import TooltipContainer from '@/ui-kit/components/Tooltip/TooltipContainer';

// Boolean attributes render through this card too, and `value` is only ever handed back
// to `onSelect` or stamped onto `data-value`, so it needs no narrower constraint.
type ItemValue = string | number | boolean;

/** Figma "Hint on always on state clicking" — the hint auto-dismisses after 3 seconds. */
const ALWAYS_ON_HINT_DURATION_MS = 3000;

interface IProps<T extends ItemValue> {
  orientation?: 'horizontal' | 'vertical';
  label?: string;
  description?: string;
  hasTooltip?: boolean;
  cost?: string;
  icon?: React.JSX.Element;
  imageSrc?: string;
  imageFit?: 'cover' | 'contain';
  imageType?: 'image' | 'icon';
  value: T;
  hasCheck?: boolean;
  /**
   * When selected & disabled, has specific appearance
   */
  hasAlwaysOn?: boolean;
  /**
   * Already-translated text shown for 3s when an always-on card is clicked. Passed in rather
   * than resolved here: `Card` is presentational and also renders outside any translator.
   */
  alwaysOnHint?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onTooltipClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSelect?: (value: T) => void;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
}

export const Card = <T extends ItemValue>({
  className,
  icon,
  imageSrc,
  imageFit,
  imageType,
  label,
  description,
  hasTooltip,
  cost,
  value,
  hasCheck,
  hasAlwaysOn,
  alwaysOnHint,
  onClick,
  onTooltipClick,
  onSelect,
  selected,
  disabled,
  orientation = 'vertical',
}: IProps<T>) => {
  const supportsHover = useMediaQuery('(hover: hover)');

  const hasImage = icon || imageSrc;
  const hasText = label || description || cost;
  const isAlwaysOn = hasAlwaysOn && selected && disabled;
  const hasHoverTooltipButton = supportsHover && hasTooltip && orientation === 'vertical';

  const hintRef = usePopoverRef();
  const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hintPlacement, setHintPlacement] = useState<Placement>('top');
  const showsHint = Boolean(isAlwaysOn && alwaysOnHint);

  useEffect(() => {
    return () => {
      clearTimeout(hintTimerRef.current ?? undefined);
    };
  }, []);

  const handleTooltipClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      onTooltipClick?.(event);
      event.currentTarget.blur();
    },
    [onTooltipClick],
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (showsHint) {
      hintRef.current?.open();
      clearTimeout(hintTimerRef.current ?? undefined);
      hintTimerRef.current = setTimeout(() => hintRef.current?.close(), ALWAYS_ON_HINT_DURATION_MS);
    }

    onClick?.(event);
    onSelect?.(value);
  };

  const card = (
    <div
      data-card
      data-value={value}
      data-selected={selected ? '' : undefined}
      className={classNames(
        'cp-card',
        {
          'cp-card--selected': selected,
          'cp-card--alwaysOn': isAlwaysOn,
          'cp-card--disabled': disabled,
        },
        className,
      )}
    >
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={classNames('cp-card__content', {
          'cp-card__content--horizontal': orientation === 'horizontal',
          'cp-card__content--disabled': disabled,
        })}
        onClick={handleClick}
        aria-disabled={disabled}
      >
        {hasCheck ? (
          <div
            className={classNames('cp-card__check', {
              'cp-card__check--selected': selected,
              'cp-card__check--disabled': disabled,
            })}
          />
        ) : null}
        {hasImage ? (
          <div className={classNames('cp-card__image-wrapper')}>
            {hasHoverTooltipButton && (
              <div className={'cp-card__tooltip-wrapper'}>
                <Button variant="secondary" iconStart="info" onClick={handleTooltipClick} />
              </div>
            )}
            {icon}
            {imageSrc && (
              <img
                className={classNames('cp-card__image', {
                  'cp-card__image--icon': imageType === 'icon',
                  'cp-card__image--horizontal': orientation === 'horizontal',
                })}
                style={imageFit ? { objectFit: imageFit } : undefined}
                src={imageSrc}
                alt={label ?? description}
              />
            )}
          </div>
        ) : null}
        {hasText ? (
          <div className={classNames('cp-card__text')}>
            {!hasImage && hasHoverTooltipButton && (
              <div className={'cp-card__tooltip-wrapper'}>
                <Button variant="secondary" iconStart="info" onClick={handleTooltipClick} />
              </div>
            )}
            {label ? <div className={'cp-card__title'}>{label}</div> : null}
            {description ? <div className={'cp-card__description'}>{description}</div> : null}
            {cost ? <div className={'cp-card__cost body-lg'}>{cost}</div> : null}
          </div>
        ) : null}
        {hasTooltip && orientation === 'horizontal' && (
          <div className={'cp-card__tooltip-wrapper--horizontal'}>
            <Button variant="secondary" iconStart="info" onClick={handleTooltipClick} />
          </div>
        )}
      </div>
    </div>
  );

  if (!showsHint) {
    return card;
  }

  return (
    <Popover
      ref={hintRef}
      trigger="none"
      placement="top"
      // the tongue supplies the gap, so popper must not add its default 4px
      padding={{ y: 0 }}
      onPlacementChange={setHintPlacement}
      animate
      content={
        <div
          className={classNames('cp-card__hint', {
            'cp-card__hint--flipped': hintPlacement.startsWith('bottom'),
          })}
        >
          <TooltipContainer size="small">{alwaysOnHint}</TooltipContainer>
        </div>
      }
    >
      {card}
    </Popover>
  );
};
